// Local-first drafts + offline submission queue for shift reports.
//
// Storage rationale: drafts must survive accidental tab closes and offline
// periods (the whole point of caregiver-first UX). They're scoped per user
// AND per client and wiped on logout via wipeAllDrafts() (see auth context).
// They contain caregiver notes only — never credentials, tokens, or PII
// beyond what the caregiver is mid-way through typing.
// codeql[js/clear-text-storage-of-sensitive-data]: justified — see comment above.
import api from "@/lib/api";

const DRAFT_PREFIX = "hc_draft_";
const QUEUE_KEY = "hc_pending_reports";

function currentUserId() {
  try {
    const raw = localStorage.getItem("hc_user_profile_cache");
    return raw ? JSON.parse(raw)?.id || "anon" : "anon";
  } catch {
    return "anon";
  }
}

function draftKey(clientId) {
  return `${DRAFT_PREFIX}${currentUserId()}_${clientId}`;
}

export function loadDraft(clientId) {
  try {
    const raw = localStorage.getItem(draftKey(clientId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function saveDraft(clientId, data) {
  try {
    localStorage.setItem(
      draftKey(clientId),
      JSON.stringify({ ...data, _updated: Date.now() })
    );
  } catch {
    /* quota — ignore */
  }
}

export function clearDraft(clientId) {
  localStorage.removeItem(draftKey(clientId));
}

/** Wipe ALL draft + queue keys (used on logout for shared-device PHI safety). */
export function wipeAllDrafts() {
  try {
    const toRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(DRAFT_PREFIX)) toRemove.push(k);
    }
    toRemove.forEach((k) => localStorage.removeItem(k));
    localStorage.removeItem(QUEUE_KEY);
  } catch {
    /* ignore */
  }
}

function readQueue() {
  try {
    return JSON.parse(localStorage.getItem(QUEUE_KEY) || "[]");
  } catch {
    return [];
  }
}

function writeQueue(items) {
  localStorage.setItem(QUEUE_KEY, JSON.stringify(items));
}

export function getQueueCount() {
  return readQueue().length;
}

export function enqueueReport(payload, clientName) {
  const items = readQueue();
  items.push({
    id: `q_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    payload,
    client_name: clientName,
    queued_at: new Date().toISOString(),
  });
  writeQueue(items);
}

/** Try the API; if it's a network error queue the payload and return {queued:true}. */
export async function submitOrQueue(payload, clientName, url = "/reports") {
  try {
    const { data } = await api.post(url, payload);
    return { submitted: true, data };
  } catch (err) {
    const isNetwork = !err?.response; // axios sets response only for HTTP responses
    if (isNetwork) {
      enqueueReport(payload, clientName);
      return { queued: true };
    }
    throw err;
  }
}

/** Attempt to flush all queued submissions. Returns {sent, failed}. */
export async function flushQueue() {
  const items = readQueue();
  if (items.length === 0) return { sent: 0, failed: 0 };
  const remaining = [];
  let sent = 0;
  for (const item of items) {
    try {
      await api.post("/reports", item.payload);
      sent += 1;
    } catch (err) {
      // network failure → keep; server-side rejection → drop to avoid infinite loop
      if (!err?.response) {
        remaining.push(item);
      }
    }
  }
  writeQueue(remaining);
  return { sent, failed: remaining.length };
}
